/*
    Congito works through callbacks depending on the current state of the user.
    eg Calling login for a user that requires a TOTP will trigger a differnet
    callback to a user that does not require a TOTP.
    
    In order to accomodate all circumstances, your callback must be an object
    that contains methods that can deal with those circumstances.
    
    So that we can control the state of the component at the same time as
    managing the user flow, we have made the callback object the component and 
    attached all the methods to 'this'. Doing this means we can call 'set state'
    which, in turn, allows us to render child components and pass methods into 
    the child components. These methods are bound to the parent and are still 
    able to manipulate it's state.
*/

import React, { Component } from "react"
import {
  CookieStorage,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js"
import PropTypes from "prop-types"
import { toCanvas } from "qrcode"
import { Route, Switch, Redirect } from "react-router-dom"
import { Card } from "ksc-portal-common-components/shadcn"
import { sonner } from "common/utils"
import { makeUnauthPostRequest } from "../../utils/lambda/unauthRequests"
import { PRIVACY_POLICY_CONST, LEGAL_CONST } from "../Legal/constants"
import {
  LOGIN,
  ENTER_TOTP,
  FORGOTTEN_PASSWORD,
  NEW_PASSWORD,
  REGISTER_TOTP,
  CHECK_EMAIL,
  REPLACE_PASSWORD,
  ADMIN_RESET,
  CODE_EXPIRED,
  FAILURE,
  SUCCESS,
  EXCEPTION_STATUS_CODE_STRINGS,
} from "./constants"
import CodeExpired from "./authViews/codeExpired"
import CheckEmail from "./authViews/checkEmail"
import FailureComp from "./authViews/forgottenPassword/failure"
import SuccessComp from "./authViews/forgottenPassword/success"
import EnterTOTP from "./authViews/enterTotp"
import ForgottenPassword from "./authViews/forgottenPassword"
import NewPassword from "./authViews/newPassword"
import AdminReset from "./authViews/adminReset"
import RegisterTOTP from "./authViews/registerTotp"
import SubmitPasswordReplacement from "./authViews/submitPasswordReplacement"
import HUBLogin from "./authViews/HUBLogin"
import LoginHeroVideo from "./authViews/LoginHeroVideo"

class LoginComponent extends Component {
  constructor(props, context) {
    super(props, context)
    this.state = {
      qrCode: null,
      searchObject: null,
      errorMessage: "",
      username: "",
    }
    this.doLogin = this.doLogin.bind(this)
    this.onFailure = this.onFailure.bind(this)
    this.onSuccess = this.onSuccess.bind(this)
    this.forgotPassword = this.forgotPassword.bind(this)
    this.newPasswordRequired = this.newPasswordRequired.bind(this)
    this.associateSecretCode = this.associateSecretCode.bind(this)
    this.enterNewPassword = this.enterNewPassword.bind(this)
    this.enterTOTPCode = this.enterTOTPCode.bind(this)
    this.mfaSetup = this.mfaSetup.bind(this)
    this.drawQRCode = this.drawQRCode.bind(this)
    this.totpRequired = this.totpRequired.bind(this)
    this.registerTOTPCode = this.registerTOTPCode.bind(this)
    this.updateUsername = this.updateUsername.bind(this)
    this.updateTotpCode = this.updateTotpCode.bind(this)
    this.updateResetUsername = this.updateResetUsername.bind(this)
    this.submitReplacementPassword = this.submitReplacementPassword.bind(this)
    this.renderForgottenPassword = this.renderForgottenPassword.bind(this)
    this.clearErrorMessage = this.clearErrorMessage.bind(this)
    this.renderPrivacyPolicy = this.renderPrivacyPolicy.bind(this)
    this.backToLogin = this.backToLogin.bind(this)
  }

  componentDidMount() {
    const { location } = this.props
    const searchQuery = decodeURIComponent(location.search.slice(1))

    if (!searchQuery || searchQuery?.includes("/logout"))
      return this.setState({ searchObject: {} })

    return this.setState({
      searchObject: {
        ...searchQuery.split("&").reduce((acc, curr) => {
          const [key, value] = curr.split(/=(.+)/)
          return { ...acc, [key]: value?.replace(/^.*\/\/[^/]+/, "") }
        }, {}),
        refer: searchQuery.split("refer=")[1],
      },
    })
  }

  onSuccess(session) {
    const {
      userPool,
      storeUserSession,
      location,
      history,
      cookies,
      redirect,
      renderApplication,
    } = this.props
    const {
      searchObject: { redirect_uri, refer },
    } = this.state
    if (redirect_uri) renderApplication(false)
    storeUserSession({
      userPool,
      session,
      cookies,
    })

    redirect({ history, redirect_uri, refer, location })
  }

  onFailure({ code, message }) {
    const { history } = this.props
    if (EXCEPTION_STATUS_CODE_STRINGS.includes(code)) {
      history.push("/error/500")
    } else {
      this.setState({ errorMessage: message })
      sonner.error(message)
    }
  }

  clearErrorMessage() {
    this.setState({ errorMessage: "" })
  }

  mfaSetup(/* challengeName, challengeParameters */) {
    this.cognitoUser.associateSoftwareToken(this)
  }

  associateSecretCode(secretCode) {
    const { history } = this.props
    const { username } = this.state
    this.clearErrorMessage()
    const { value } = username
    history.push(`/${LOGIN}/${REGISTER_TOTP}`)
    this.setState(
      {
        errorMessage: null,
        qrCode: `otpauth://totp/AWSCognito:${value}?secret=${secretCode}&issuer=KaizenReporting`,
      },
      this.drawQRCode
    )
  }

  drawQRCode() {
    const { qrCode } = this.state
    if (qrCode)
      toCanvas(document.getElementById("login-qr-canvas"), qrCode, (err) => {
        if (err) console.error(err)
      })
  }

  enterNewPassword(value) {
    this.cognitoUser.completeNewPasswordChallenge(value, {}, this)
  }

  newPasswordRequired() {
    const { history } = this.props
    this.clearErrorMessage()
    history.push(`/${LOGIN}/${NEW_PASSWORD}`)
  }

  totpRequired(/* secretCode */) {
    const { history } = this.props
    this.clearErrorMessage()
    history.push(`/${LOGIN}/${ENTER_TOTP}${window.location.search}`)
  }

  enterTOTPCode(e) {
    if (e) e.preventDefault()
    this.clearErrorMessage()
    const { value } = this.totpCode
    try {
      this.cognitoUser.sendMFACode(value, this, "SOFTWARE_TOKEN_MFA")
    } catch (error) {
      console.error(error)
    }
  }

  registerTOTPCode(e) {
    if (e) e.preventDefault()
    this.clearErrorMessage()
    const { value } = this.totpCode
    try {
      this.cognitoUser.verifySoftwareToken(value, "My TOTP Code", this)
    } catch (error) {
      console.error(error)
    }
  }

  doLogin({ email, password }) {
    const { userPool, applicationClientId } = this.props
    if (!userPool?.userPoolId || !applicationClientId) return
    this.clearErrorMessage()
    const { username } = this.state
    const domain = window.location.host.split(":")[0]

    this.cognitoUser = new CognitoUser({
      Username: email || username,
      Pool: userPool,
      Storage: new CookieStorage({ domain }),
    })
    this.cognitoUser.authenticateUser(
      new AuthenticationDetails({
        Username: email || username,
        Password: password,
      }),
      this
    )
  }

  forgotPassword(username) {
    this.clearErrorMessage()

    const { history } = this.props

    const resetUsername = username || this.resetUsername.value.toLowerCase()

    makeUnauthPostRequest({
      url: "/foundation_v1/forgot-password",
      body: {
        email_address: resetUsername,
      },
      onSuccess: () => {
        this.setState({
          errorMessage: null,
        })
      },
      onFailure: ({ data: { error } }) => this.onFailure(error),
    })
    history.push(`/${LOGIN}/${CHECK_EMAIL}/${resetUsername}`)
  }

  submitReplacementPassword(value) {
    const { history, userPool } = this.props
    const {
      searchObject: { code, username },
    } = this.state
    const userData = {
      Username: username,
      Pool: userPool,
    }
    this.cognitoUser = new CognitoUser(userData)

    this.clearErrorMessage()

    const that = this
    this.cognitoUser.confirmPassword(code, value, {
      onSuccess: () => {
        history.push(`/${LOGIN}/${SUCCESS}`)
        that.setState({
          errorMessage: null,
        })
      },
      onFailure: ({ message }) => {
        if (message?.toLowerCase().startsWith("invalid")) {
          history.push(`/${LOGIN}/${CODE_EXPIRED}`)
        } else {
          this.setState({ errorMessage: message })
          history.push(`/${LOGIN}/${FAILURE}`)
        }
      },
    })
  }

  updateUsername(e) {
    const {
      target: { value },
    } = e
    this.setState({
      username: value,
    })
  }

  updateTotpCode(input) {
    this.totpCode = input
  }

  updateResetUsername(input) {
    this.resetUsername = input
    this.forceUpdate()
  }

  backToLogin() {
    const { history } = this.props
    history.push(`/${LOGIN}`)
    this.clearErrorMessage()
  }

  renderForgottenPassword() {
    const { history } = this.props
    history.push(`/${LOGIN}/${FORGOTTEN_PASSWORD}`)
    this.clearErrorMessage()
  }

  renderPrivacyPolicy() {
    const { history } = this.props
    history.push(
      `/${LEGAL_CONST}/${PRIVACY_POLICY_CONST.toLowerCase().replaceAll(
        " ",
        "-"
      )}`
    )
  }

  render() {
    const {
      passwordCriteria,
      storePasswordCriteria,
      userPool,
      applicationClientId,
    } = this.props
    const { qrCode, errorMessage, searchObject, username } = this.state
    const submitButtonDisabled = !(userPool?.userPoolId && applicationClientId)

    return (
      <div className="relative flex items-center justify-center min-h-screen overflow-hidden">
        <LoginHeroVideo renderPrivacyPolicy={this.renderPrivacyPolicy}>
          <Card className="w-full lg:w-1/2 max-w-sm backdrop-blur-sm bg-background/80">
            <Switch>
              <Route exact path={`/${LOGIN}`}>
                {searchObject && (
                  <HUBLogin
                    renderForgottenPassword={this.renderForgottenPassword}
                    errorMessage={errorMessage}
                    searchObject={searchObject}
                    doLogin={this.doLogin}
                    renderPrivacyPolicy={this.renderPrivacyPolicy}
                    submitButtonDisabled={submitButtonDisabled}
                  />
                )}
              </Route>
              <Route exact path={`/${LOGIN}/${NEW_PASSWORD}`}>
                <NewPassword
                  authDivInnerClassName="auth-div-inner-style-padding"
                  clearErrorMessage={this.clearErrorMessage}
                  enterNewPassword={this.enterNewPassword}
                  updateNewPassword={this.updateNewPassword}
                  cognitoUser={this.cognitoUser}
                  backToLogin={this.backToLogin}
                />
              </Route>
              <Route exact path={`/${LOGIN}/${REGISTER_TOTP}`}>
                <RegisterTOTP
                  authDivInnerClassName="auth-div-inner-style-no-padding"
                  qrCode={qrCode}
                  registerTOTPCode={this.registerTOTPCode}
                  updateTotpCode={this.updateTotpCode}
                  errorMessage={errorMessage}
                  backToLogin={this.backToLogin}
                />
              </Route>
              <Route exact path={`/${LOGIN}/${FORGOTTEN_PASSWORD}`}>
                <ForgottenPassword
                  backToLogin={this.backToLogin}
                  email={this.resetUsername}
                  usernameDefault={username || ""}
                  authDivInnerClassName="auth-div-inner-style-padding"
                  updateResetUsername={this.updateResetUsername}
                  forgotPassword={this.forgotPassword}
                />
              </Route>
              <Route exact path={`/${LOGIN}/${ENTER_TOTP}`}>
                <EnterTOTP
                  errorMessage={errorMessage}
                  authDivInnerClassName="auth-div-inner-style-padding"
                  updateTotpCode={this.updateTotpCode}
                  enterTOTPCode={this.enterTOTPCode}
                  qrCode={qrCode}
                  backToLogin={this.backToLogin}
                />
              </Route>
              <Route exact path={`/${LOGIN}/${REPLACE_PASSWORD}`}>
                <SubmitPasswordReplacement
                  authDivInnerClassName="auth-div-inner-style-padding"
                  submitReplacementPassword={this.submitReplacementPassword}
                  storePasswordCriteria={storePasswordCriteria}
                  passwordCriteria={passwordCriteria}
                  backToLogin={this.backToLogin}
                />
              </Route>
              <Route exact path={`/${LOGIN}/${CHECK_EMAIL}/:username`}>
                <CheckEmail
                  backToLogin={this.backToLogin}
                  authDivInnerClassName="auth-dev-inner-style-padding"
                />
              </Route>
              <Route exact path={`/${LOGIN}/${ADMIN_RESET}`}>
                <AdminReset
                  authDivInnerClassName="auth-div-inner-style-no-padding"
                  backToLogin={this.backToLogin}
                />
              </Route>
              <Route exact path={`/${LOGIN}/${CODE_EXPIRED}`}>
                {searchObject && (
                  <CodeExpired
                    authDivInnerClassName="auth-div-inner-style-no-padding"
                    backToLogin={this.backToLogin}
                    forgotPassword={this.forgotPassword}
                    searchObject={searchObject}
                  />
                )}
              </Route>
              <Route exact path={`/${LOGIN}/${FAILURE}`}>
                <FailureComp
                  backToLogin={this.backToLogin}
                  errorMessage={errorMessage}
                />
              </Route>
              <Route exact path={`/${LOGIN}/${SUCCESS}`}>
                <SuccessComp backToLogin={this.backToLogin} />
              </Route>
              <Redirect to={`/${LOGIN}`} />
            </Switch>
          </Card>
        </LoginHeroVideo>
      </div>
    )
  }
}

LoginComponent.propTypes = {
  userPool: PropTypes.shape({
    userPoolId: PropTypes.string,
  }),
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  storeUserSession: PropTypes.func,
  history: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ push: PropTypes.func }),
  ]).isRequired,
  cookies: PropTypes.shape({
    set: PropTypes.func.isRequired,
  }).isRequired,
  applicationClientId: PropTypes.string,
  redirect: PropTypes.func.isRequired,
  renderApplication: PropTypes.func.isRequired,
  storePasswordCriteria: PropTypes.func.isRequired,
  passwordCriteria: PropTypes.shape({}),
}

LoginComponent.defaultProps = {
  userPool: { userPoolId: "" },
  storeUserSession: () => null,
  passwordCriteria: null,
  applicationClientId: null,
}

export default LoginComponent
